import { store } from 'src/store/configureStore'
import {
  findCharacterizationIdFromName,
  findIngredientIdFromName,
  findLabLocationIdFromName,
  findParameterCategoryFromId,
  findProcessingIdFromName,
  findProjectIdFromName,
  findPropertiesIdFromName,
  findUserIdFromUserName,
  findWorkorderIdFromName
} from '../convertHelper'
import { apiCallHelper } from '../apiCallHelper'
import { filterApi } from 'src/services/formulation'
import { Flex, Tag, Typography } from 'antd';
import { ToolsImplementationsType } from '../AiAssistantHelper'
import { useState } from 'react'
import { StyledButton } from 'src/styled_components/StyledButton'
import { compareFormulationsRequest } from 'src/store/actions/compareFormulations'
import { dataExportRequest } from 'src/store/actions/workOrderDetails'
import { history } from 'src'
import useTranslate from 'src/utils/useTranslate'
import { ApartmentOutlined, FileExcelOutlined, LineChartOutlined } from '@ant-design/icons'

export const formulationFilterToolsSchema = () => {
  const workorders = store.getState().workOrders.data
  const projectNames = store.getState().projects.projectList.map((project: any) => project.name)

  const workOrderNames = workorders.map((workorder: any) => workorder.work_order_name)

  const stages = [...new Set(workorders.flatMap((wo: any) => wo.stages?.map((res: any, index: number) => index)))].map(
    (res: any, index: number) => `Stage ${index + 1}`
  )

  const teams = store.getState().teams.data

  const user_name = teams?.map(({ user_id, user_name }: any, index: number) => user_name)

  const displayNames = store.getState().displayNames.data

  const applications = displayNames?.application
  const orderTypes = displayNames?.order_type
  const grade = displayNames?.grade

  const labsLocationList = store.getState().settings.labsLocationList
  const labsLocationNames = labsLocationList?.map((location: any) => location.name)

  const materialNames = Object.values(displayNames?.material || {})?.map((material: any) => material?.name)

  return [
    {
      type: 'function',
      function: {
        name: 'get_formulations',
        description: 'Get all formulations',
        parameters: {
          type: 'object',
          properties: {
            operator: {
              type: 'string',
              enum: ['and', 'or'],
              default: 'and'
            },
            project_name: {
              type: 'array',
              items: {
                type: 'string',
                enum: projectNames
              }
            },
            filters: {
              type: 'array',
              items: {
                type: 'object',
                oneOf: [
                  // Properties
                  {
                    required: ['properties_details'],
                    properties: {
                      properties_details: {
                        type: 'object',
                        properties: {
                          properties_name: {
                            type: 'array',
                            items: {
                              type: 'string'
                            }
                          },
                          value_details: {
                            type: 'object',
                            oneOf: [
                              {
                                properties: {
                                  val: { type: 'number' },
                                  max: {
                                    type: 'number',
                                    description: 'Max value',
                                    default: null
                                  },
                                  operator: {
                                    type: 'string',
                                    enum: ['eq', 'gt', 'lt', 'range']
                                  }
                                },
                                description: 'Numerical value details for the properties',
                                required: ['val', 'operator'],
                                additionalProperties: false
                              },
                              {
                                properties: {
                                  val: {
                                    type: 'boolean',
                                    description: 'True or False, present or not present'
                                  },
                                  operator: {
                                    type: 'string',
                                    enum: ['exists']
                                  }
                                },
                                description: 'Boolean value details for the properties',
                                required: ['val', 'operator'],
                                additionalProperties: false
                              }
                            ]
                          }
                        },
                        required: ['properties_name', 'value_details']
                      }
                    }
                  },
                  // Ingredient
                  {
                    required: ['ingredient_details'],
                    properties: {
                      ingredient_details: {
                        type: 'object',
                        properties: {
                          ingredient_name: {
                            type: 'string'
                          },
                          value_details: {
                            type: 'object',
                            oneOf: [
                              {
                                properties: {
                                  val: { type: 'number' },
                                  max: {
                                    type: 'number',
                                    description: 'Max value',
                                    default: null
                                  },
                                  operator: {
                                    type: 'string',
                                    enum: ['eq', 'gt', 'lt', 'range']
                                  }
                                },
                                description: 'Numerical value details for the ingredient',
                                required: ['val', 'operator'],
                                additionalProperties: false
                              },
                              {
                                properties: {
                                  val: {
                                    type: 'boolean',
                                    description: 'True or False, present or not present'
                                  },
                                  operator: {
                                    type: 'string',
                                    enum: ['exists']
                                  }
                                },
                                description: 'Boolean value details for the ingredient',
                                required: ['val', 'operator'],
                                additionalProperties: false
                              }
                            ]
                          }
                        },
                        required: ['ingredient_name', 'value_details']
                      }
                    }
                  },
                  // Processing
                  {
                    required: ['processing_details'],
                    properties: {
                      processing_details: {
                        type: 'object',
                        properties: {
                          processing_name: {
                            type: 'string'
                          },
                          value_details: {
                            type: 'object',
                            oneOf: [
                              {
                                properties: {
                                  val: { type: 'number' },
                                  max: {
                                    type: 'number',
                                    description: 'Max value',
                                    default: null
                                  },
                                  operator: {
                                    type: 'string',
                                    enum: ['eq', 'gt', 'lt', 'range']
                                  }
                                },
                                description: 'Numerical value details for the processing parameter',
                                required: ['val', 'operator'],
                                additionalProperties: false
                              },
                              {
                                properties: {
                                  val: {
                                    type: 'boolean',
                                    description: 'True or False, present or not present'
                                  },
                                  operator: {
                                    type: 'string',
                                    enum: ['exists']
                                  }
                                },
                                description: 'Boolean value details for the processing parameter',
                                required: ['val', 'operator'],
                                additionalProperties: false
                              }
                            ]
                          }
                        },
                        required: ['processing_name', 'value_details']
                      }
                    }
                  },
                  // Characterization
                  {
                    required: ['characterization_details'],
                    properties: {
                      characterization_details: {
                        type: 'object',
                        properties: {
                          characterization_name: {
                            type: 'string'
                          },
                          value_details: {
                            type: 'object',
                            oneOf: [
                              {
                                properties: {
                                  val: { type: 'number' },
                                  max: {
                                    type: 'number',
                                    description: 'Max value',
                                    default: null
                                  },
                                  operator: {
                                    type: 'string',
                                    enum: ['eq', 'gt', 'lt', 'range']
                                  }
                                },
                                description: 'Numerical value details for the characterization',
                                required: ['val', 'operator'],
                                additionalProperties: false
                              },
                              {
                                properties: {
                                  val: {
                                    type: 'boolean',
                                    description: 'True or False, present or not present'
                                  },
                                  operator: {
                                    type: 'string',
                                    enum: ['exists']
                                  }
                                },
                                description: 'Boolean value details for the characterization',
                                required: ['val', 'operator'],
                                additionalProperties: false
                              }
                            ]
                          }
                        },
                        required: ['characterization_name', 'value_details']
                      }
                    }
                  },
                  {
                    required: ['work_order_name'],
                    properties: {
                      work_order_name: {
                        type: 'array',
                        items: {
                          type: 'string',
                          enum: workOrderNames
                        }
                      }
                    }
                  },
                  {
                    required: ['stage'],
                    properties: {
                      stage: {
                        type: 'array',
                        items: {
                          type: 'string',
                          enum: stages
                        }
                      }
                    }
                  },
                  {
                    required: ['user_name'],
                    properties: {
                      user_name: {
                        type: 'array',
                        items: {
                          type: 'string',
                          enum: user_name
                        }
                      }
                    }
                  },
                  {
                    required: ['application'],
                    properties: {
                      application: {
                        type: 'array',
                        items: {
                          type: 'string',
                          enum: applications
                        }
                      }
                    }
                  },
                  {
                    required: ['order_type'],
                    properties: {
                      order_type: {
                        type: 'array',
                        items: {
                          type: 'string',
                          enum: orderTypes
                        }
                      }
                    }
                  },
                  {
                    required: ['grade'],
                    properties: {
                      grade: {
                        type: 'array',
                        items: {
                          type: 'string',
                          enum: grade
                        }
                      }
                    }
                  },
                  {
                    required: ['lab_locations_names'],
                    properties: {
                      lab_locations_names: {
                        type: 'array',
                        items: {
                          type: 'string',
                          enum: labsLocationNames
                        }
                      }
                    }
                  },
                  {
                    required: ['material'],
                    properties: {
                      material: {
                        type: 'array',
                        items: {
                          type: 'string',
                          enum: materialNames
                        }
                      }
                    }
                  }
                ]
              }
            }
          },
          required: ['operator'],
          additionalProperties: false
        }
      }
    }
  ]
}

export const formulationFilterToolsImplementation: () => ToolsImplementationsType = () => {
  return {
    get_formulations: async (args: any) => {
      const { operator: selector, project_name, filters } = args

      const workorders = store.getState().workOrders.data

      const project_id = project_name?.map((name: string) => findProjectIdFromName(name)).filter((id: any) => id !== undefined && id !== null)

      const actualFilters: {
        category: string
        val: string | number | boolean | null | string[]
        operator: string
        max: number | null
        parameter: string
        parameter_type: string
        selector: string
      }[] = []

      filters?.forEach((filter: any) => {
        const {
          properties_details,
          ingredient_details,
          processing_details,
          characterization_details,
          work_order_name,
          stage,
          user_name,
          application,
          order_type,
          grade,
          lab_locations_names,
          material
        } = filter

        if (properties_details) {
          const { properties_name, value_details } = properties_details
          const { val, max, operator } = value_details
          const parameterType = 'properties'

          const propertiesIds = properties_name
            .map((name: string) => findPropertiesIdFromName(name))
            .filter((id: any) => id !== undefined && id !== null)

          const category = propertiesIds.map((id: any) => findParameterCategoryFromId(id, parameterType) || parameterType)

          if (propertiesIds.length > 0) {
            actualFilters.push({
              category: category,
              val: val,
              operator: operator,
              max: max || null,
              parameter: propertiesIds,
              parameter_type: parameterType,
              selector: operator
            })
          }
        }

        if (ingredient_details) {
          const { ingredient_name, value_details } = ingredient_details
          const { val, max, operator } = value_details
          const parameterType = 'ingredients'

          const ingredientId = findIngredientIdFromName(ingredient_name)
          const category = findParameterCategoryFromId(ingredientId, parameterType) || parameterType

          if (ingredientId) {
            actualFilters.push({
              category,
              val: val,
              operator: operator,
              max: max || null,
              parameter: ingredientId,
              parameter_type: parameterType,
              selector: operator
            })
          }
        }

        if (processing_details) {
          const { processing_name, value_details } = processing_details
          const { val, max, operator } = value_details
          const parameterType = 'processing'

          const processingId = findProcessingIdFromName(processing_name)
          const category = findParameterCategoryFromId(processingId, parameterType) || parameterType

          if (processingId) {
            actualFilters.push({
              category,
              val: val,
              operator: operator,
              max: max || null,
              parameter: processingId,
              parameter_type: parameterType,
              selector: operator
            })
          }
        }
        if (characterization_details) {
          const { characterization_name, value_details } = characterization_details
          const { val, max, operator } = value_details
          const parameterType = 'characterization'

          const characterizationId = findCharacterizationIdFromName(characterization_name)
          const category = findParameterCategoryFromId(characterizationId, parameterType) || parameterType

          if (characterizationId) {
            actualFilters.push({
              category,
              val: val,
              operator: operator,
              max: max || null,
              parameter: characterizationId,
              parameter_type: parameterType,
              selector: operator
            })
          }
        }

        // With operator 'in', the value is an array of strings
        if (work_order_name) {
          const workOrderId = work_order_name
            .map((name: string) => findWorkorderIdFromName(name))
            .filter((id: any) => id !== undefined && id !== null)
          actualFilters.push({
            category: 'work_order_id',
            val: workOrderId,
            operator: 'in',
            max: null,
            parameter: 'work_order_id',
            parameter_type: 'work_order_id',
            selector
          })
        }

        if (stage) {
          const stagesData = [...new Set(workorders.flatMap((wo: any) => wo.stages?.map((res: any, index: number) => index)))].map(
            (res: any, index: number) => ({
              name: `Stage ${index + 1}`,
              key: index
            })
          )

          const stagesIndex = stage
            .map((name: string) => {
              const stageData = stagesData.find((stage: any) => stage.name === name)
              return stageData ? stageData.key : null
            })
            .filter((id: any) => id !== undefined && id !== null)

          const stageIds = workorders
            .map((wo: any) => stagesIndex.map((id: any) => wo?.stages?.[id]?.identifier))
            .flatMap((id: any) => id)
            .filter((id: any) => !!id)
          actualFilters.push({
            category: 'stage',
            val: stageIds,
            operator: 'in',
            max: null,
            parameter: 'stage',
            parameter_type: 'stage',
            selector
          })
        }

        if (lab_locations_names) {
          const labLocationIds = lab_locations_names
            ?.map((name: string) => findLabLocationIdFromName(name))
            .filter((id: any) => id !== undefined && id !== null)
          actualFilters.push({
            category: 'lab_locations',
            val: labLocationIds,
            operator: 'in',
            max: null,
            parameter: 'lab_locations',
            parameter_type: 'lab_locations',
            selector
          })
        }

        if (user_name) {
          const userIds = user_name?.map((name: string) => findUserIdFromUserName(name)).filter((id: any) => id !== undefined && id !== null)
          actualFilters.push({
            category: 'user_id',
            val: userIds,
            operator: 'in',
            max: null,
            parameter: 'user_id',
            parameter_type: 'user_id',
            selector
          })
        }

        // With operator 'eq'

        if (application) {
          actualFilters.push({
            category: 'application',
            val: application,
            operator: 'eq',
            max: null,
            parameter: 'application',
            parameter_type: 'application',
            selector
          })
        }

        if (order_type) {
          actualFilters.push({
            category: 'order_type',
            val: order_type,
            operator: 'eq',
            max: null,
            parameter: 'order_type',
            parameter_type: 'order_type',
            selector
          })
        }

        if (grade) {
          actualFilters.push({
            category: 'grade',
            val: grade,
            operator: 'eq',
            max: null,
            parameter: 'grade',
            parameter_type: 'grade',
            selector
          })
        }

        if (material) {
          actualFilters.push({
            category: 'material',
            val: material,
            operator: 'eq',
            max: null,
            parameter: 'material',
            parameter_type: 'material',
            selector
          })
        }
      })

      const filtersPayload =
        actualFilters.length === 0
          ? null
          : {
              data: [
                {
                  data: actualFilters,
                  selector
                }
              ],
              selector: 'and'
            }

      const currentProject = store.getState().projects.current

      const finalPayload = {
        ...(filtersPayload ? { filters: filtersPayload } : {}),
        project_id: project_id?.length > 0 ? project_id : [currentProject]
      }

      return await apiCallHelper({
        request: filterApi,
        params: finalPayload,
        nodeGenerator: (data: any) => {
          const { formulation_data } = data

          const selected: string[] = []

          return (
            <Flex>
              {formulation_data.map((item: any) => {
                const { formulation_id, formulation_display_id } = item

                return (
                  <Tag.CheckableTag key={formulation_id} checked={selected.includes(formulation_id)} onChange={(checked: any) => {}}>
                    {formulation_display_id}
                  </Tag.CheckableTag>
                )
              })}
            </Flex>
          )
        },
        StatefulNodeComponent: (props: any) => {
          const {
            data: { formulation_data }
          } = props
          const [selected, setSelected] = useState<string[]>([])
          const [t] = useTranslate()

          const selectedWorkOrders = new Set<string>()
          const selectedProjects = new Set<string>()
          const selectedWorkorderType = new Set<string>()

          return (
            <Flex vertical gap={16} style={{ width: '100%' }}>
              <Typography.Title level={4} style={{ marginBottom: 0 }}>
                {t('common.formulations')}
              </Typography.Title>
              {!!formulation_data?.length && (
                <Flex wrap="wrap" gap={8}>
                  {formulation_data.map((item: any) => {
                    const { formulation_id, formulation_display_id, project_id, work_order_id, work_order_type } = item

                    selectedWorkOrders.add(work_order_id)
                    selectedProjects.add(project_id)
                    selectedWorkorderType.add(work_order_type)

                    return (
                      <Tag.CheckableTag
                        key={formulation_id}
                        checked={selected.includes(formulation_id)}
                        onChange={(checked: any) => {
                          if (checked) {
                            setSelected([...selected, formulation_id])
                          } else {
                            setSelected(selected.filter((id) => id !== formulation_id))
                          }
                        }}
                        style={{
                          fontSize: '0.75rem'
                        }}
                      >
                        {formulation_display_id}
                      </Tag.CheckableTag>
                    )
                  })}
                </Flex>
              )}
              {!formulation_data?.length && (
                <Typography.Text
                  style={{
                    width: '100%',
                    height: '200px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  type="secondary"
                >
                  {t('formulations.noFormulationsFound')}
                </Typography.Text>
              )}
              <Flex
                style={{
                  display: selected.length > 0 ? 'flex' : 'none',
                  transition: 'all 0.3s ease-in-out',
                  width: '100%'
                }}
                gap={'small'}
              >
                <StyledButton
                  className="animated__hover__btn"
                  onClick={() => {
                    store.dispatch(compareFormulationsRequest(selected))
                  }}
                  icon={<LineChartOutlined />}
                >
                  <span className="animated__hover__content">{t('common.compare')}</span>
                </StyledButton>

                <StyledButton
                  className="animated__hover__btn"
                  onClick={() => {
                    store.dispatch(
                      dataExportRequest({
                        formulation_ids: selected
                      })
                    )
                  }}
                  icon={<FileExcelOutlined />}
                >
                  <span className="animated__hover__content">{t('formulations.button.exportResults')}</span>
                </StyledButton>

                <StyledButton
                  className="animated__hover__btn"
                  onClick={() => {
                    history.replace('/ai-engine/new_model/custom_ml', {
                      selectedWorkOrders: Array.from(selectedWorkOrders),
                      selectedFormulations: selected,
                      isMultiStage: Array.from(selectedWorkorderType).includes('Multi Stage'),
                      project: Array.from(selectedProjects)[0],
                      redirect: true
                    })
                  }}
                  icon={
                    <ApartmentOutlined />
                  }
                >
                  <span className="animated__hover__content">{t('formulations.buildcustommodel')}</span>
                </StyledButton>
              </Flex>
            </Flex>
          )
        }
      })
    }
  }
}
